import type { Newsletter } from 'interfaces/Account';
import { type Dispatch, type JSX, type SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import subscribeUser from 'lib/personalizationApi/subscribeUser';
import unsubscribeUser from 'lib/personalizationApi/unsubscribeUser';
import * as Yup from 'yup';
import type { ValidationError } from 'yup';
import isEmail from 'validator/lib/isEmail';
import { NoticeContext } from 'providers/NoticeProvider';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { ModalButtonProps } from 'components/Modal';
import findUserInPiano from 'lib/personalizationApi/findUserInPiano';
import guestModalChildren from './GuestModalChildren';
import doiModalChildren from './DoiModalChildren';
import { eeaCountries } from './personalInfoSelectOptions';

export default function useNewslettersModal(
  email: string,
  country: { label: string; value: string },
  setEmail: Dispatch<SetStateAction<string>>,
  setCountry: Dispatch<SetStateAction<{ label: string; value: string }>>,
  setUserNewsletters: Dispatch<SetStateAction<Newsletter[]>>,
  setLoading: Dispatch<SetStateAction<string>>,
  pagePath: string,
  bypassReCaptcha?: boolean,
) {
  const { setNotification } = useContext(NoticeContext);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTarget, setModalTarget] = useState<Newsletter[]>([]);
  const [modalType, setModalType] = useState<
    | 'subscribe'
    | 'unsubscribe'
    | 'unsubscribeFromAll'
    | 'subscribeGuest'
    | 'firstSubscribeGuestSuccess'
    | 'createAccountGuest'
    | 'subscribeToAll'
    | 'subscribeAllGuest'
  >('subscribe');

  const [checked, setChecked] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [countryError, setCountryError] = useState(false);
  const [inputEmail, setInputEmail] = useState('');
  const [inputCountry, setInputCountry] = useState<{ label: string; value: string }>({ label: '', value: '' });
  const [emailExists, setEmailExists] = useState(false);
  const [showSuccessGuestRegistration, setShowSuccessGuestRegistration] = useState(false);

  const validationSchema = Yup.object().shape({
    country: Yup.string().required('Country is required'),
    email: Yup.string()
      .email('Invalid email')
      .required('Email is required')
      .test(
        'is-valid',
        () => 'Email entered is invalid',
        (value) => (value ? isEmail(value) : new Yup.ValidationError('Email entered is invalid.')),
      ),
  });

  const onModalClose = () => {
    setInputCountry({ label: '', value: '' });
    setInputEmail('');
    setChecked(false);
    setEmailError(false);
    setCountryError(false);
    setEmailExists(false);
  };

  useEffect(() => {
    if (!isModalOpen) {
      onModalClose();
    }
  }, [isModalOpen]);

  const handleRegistration = () => {
    validationSchema
      .validate({ country: inputCountry.value, email: inputEmail })
      .then(async () => {
        setEmailError(!inputEmail);
        setCountryError(!inputCountry.value);
        setEmailExists(false);
        if (checked) {
          const findUser = await findUserInPiano(inputEmail);
          if (findUser) {
            throw new Error('email exists');
          }

          Cookies.set('guest_email', inputEmail, { expires: new Date(new Date().getTime() + 60 * 60 * 1000) });
          Cookies.set('guest_country', inputCountry.value, {
            expires: new Date(new Date().getTime() + 60 * 60 * 1000),
          });
          setNotification?.(
            'success',
            `You can now subscribe to Fortune Newsletters with the guest email: ${inputEmail}`,
          );
          setEmail(inputEmail);
          setCountry(inputCountry);
          setIsModalOpen(false);
        }
      })
      .catch((error: ValidationError) => {
        // mark error when both fields values are missing since Yup provides only first error path, not all
        setEmailError(!inputEmail);
        setCountryError(!inputCountry.value);

        // handle error depending on invalid field
        if (error.path === 'email') {
          setEmailError(true);
        } else if (error.path === 'country') {
          setCountryError(true);
        } else if (error.message === 'email exists') {
          setEmailExists(true);
        }
      });
  };

  const subscribeOnRegistration = async (formName: string[]) => {
    if (!executeRecaptcha && !bypassReCaptcha) {
      console.error('executeRecaptcha is not defined.');
      return;
    }

    let recaptchaToken;
    if (!bypassReCaptcha) {
      recaptchaToken = await executeRecaptcha();
    }

    subscribeUser(inputEmail, inputCountry.value, formName, recaptchaToken || '', pagePath, bypassReCaptcha)
      .then(() => {
        setUserNewsletters((prevUserNews) =>
          prevUserNews.map((n) => {
            if (formName.length === 1 && n.formName === formName[0]) {
              return { ...n, subscriptionStatus: 'subscribed' };
            }
            if (formName.length > 1 && formName.some((newsletter) => newsletter === n.formName)) {
              return { ...n, subscriptionStatus: 'subscribed' };
            }
            return n;
          }),
        );
        setLoading('none');
        setShowSuccessGuestRegistration(true);
      })
      .catch(() => {
        setNotification?.('error', 'Your subscription could not be registered.');
        setLoading('none');
      });
  };

  const handleRegistrationOnSubscribe = (formName: string[]) => {
    validationSchema
      .validate({ country: inputCountry.value, email: inputEmail })
      .then(async () => {
        setEmailError(!inputEmail);
        setCountryError(!inputCountry.value);
        setEmailExists(false);
        if (checked) {
          const findUser = await findUserInPiano(inputEmail);
          if (findUser) {
            throw new Error('email exists');
          }

          Cookies.set('guest_email', inputEmail, { expires: new Date(new Date().getTime() + 60 * 60 * 1000) });
          Cookies.set('guest_country', inputCountry.value, {
            expires: new Date(new Date().getTime() + 60 * 60 * 1000),
          });
          setEmail(inputEmail);
          setCountry(inputCountry);
          setLoading('updating');

          const unsubscribedNewsletters = formName;

          void subscribeOnRegistration(unsubscribedNewsletters);
          setIsModalOpen(false);
        }
      })
      .catch((error: ValidationError) => {
        // mark error when both fields values are missing since Yup provides only first error path, not all
        setEmailError(!inputEmail);
        setCountryError(!inputCountry.value);
        // handle error depending on invalid field
        if (error.path === 'email') {
          setEmailError(true);
        } else if (error.path === 'country') {
          setCountryError(true);
        } else if (error.message === 'email exists') {
          setEmailExists(true);
        }
      });
  };

  const unSubscribeAll = (userEmail: string, countryValue: string, formName: string[]) => {
    unsubscribeUser(userEmail, countryValue, formName, pagePath)
      .then(() => {
        setLoading('none');

        setUserNewsletters((prevUserNews) =>
          prevUserNews.map((n) => {
            if (formName.filter((id) => id === n.formName).length > 0) {
              return { ...n, subscriptionStatus: 'recently-unsubscribed' };
            }
            return n;
          }),
        );
      })
      .catch(() => {
        setNotification?.('error', 'There was an error when trying to unsubscribe from all the newsletters.');
        setLoading('none');
      });
  };

  const modalSettings = useMemo(() => {
    const settings = {
      createAccountGuest: {
        children: guestModalChildren(
          setIsModalOpen,
          inputEmail,
          setInputEmail,
          emailError,
          inputCountry,
          setInputCountry,
          countryError,
          checked,
          setChecked,
          emailExists,
        ),
        className: '',
        firstButton: {
          onClick: handleRegistration,
          title: 'REGISTER FOR FREE',
        },
        onClose: () => {
          setIsModalOpen(false);
        },

        title: 'Enter your information to receive Fortune newsletters.',
      },
      firstSubscribeGuestSuccess: {
        children: eeaCountries.includes(country.value) ? doiModalChildren(modalTarget[0]?.formName) : '',
        className: '',
        firstButton: {
          onClick: () => {
            setIsModalOpen(false);
          },
          title: 'CONTINUE BROWSING',
        },
        onClose: () => setIsModalOpen(false),
        title: 'Thank you for subscribing.',
      },
      subscribe: {
        children: eeaCountries.includes(country.value) ? doiModalChildren(modalTarget[0]?.formName) : '',
        className: '',
        firstButton: {
          onClick: () => {
            setIsModalOpen(false);
          },
          title: 'CONTINUE BROWSING',
        },
        onClose: () => setIsModalOpen(false),
        title: 'Thank you for subscribing.',
      },
      subscribeAllGuest: {
        children: guestModalChildren(
          setIsModalOpen,
          inputEmail,
          setInputEmail,
          emailError,
          inputCountry,
          setInputCountry,
          countryError,
          checked,
          setChecked,
          emailExists,
          'all',
        ),
        className: '',
        firstButton: {
          onClick: () =>
            handleRegistrationOnSubscribe(
              modalTarget
                .filter((nl) => nl.subscriptionStatus !== 'subscribed')
                .map((newsletter) => newsletter.formName),
            ),
          title: 'REGISTER FOR FREE',
        },
        onClose: () => {
          setIsModalOpen(false);
        },
        title: 'Enter your information to receive Fortune newsletters.',
      },
      subscribeGuest: {
        children: guestModalChildren(
          setIsModalOpen,
          inputEmail,
          setInputEmail,
          emailError,
          inputCountry,
          setInputCountry,
          countryError,
          checked,
          setChecked,
          emailExists,
          modalTarget[0]?.formName,
        ),
        className: '',
        firstButton: {
          onClick: () => handleRegistrationOnSubscribe([modalTarget[0]?.formName]),
          title: 'REGISTER FOR FREE',
        },
        onClose: () => {
          setIsModalOpen(false);
        },
        success: true,
        title: 'Enter your information to receive Fortune newsletters.',
      },
      subscribeToAll: {
        children: eeaCountries.includes(country.value) ? doiModalChildren('the newsletters') : '',
        className: '',
        firstButton: {
          onClick: () => {
            setIsModalOpen(false);
          },
          title: 'CONTINUE BROWSING',
        },
        onClose: () => setIsModalOpen(false),
        title: 'Thank you for subscribing.',
      },
      unsubscribe: {
        children: '',
        className: '',
        firstButton: {
          $variant: 'secondary',
          onClick: () => {
            setLoading('updating');
            unsubscribeUser(email, country.value, [modalTarget[0].formName], pagePath)
              .then(() => {
                setLoading('none');
                setUserNewsletters((prevUserNews) =>
                  prevUserNews.map((n) => {
                    if (n.formName === modalTarget[0].formName) {
                      return { ...n, subscriptionStatus: 'recently-unsubscribed' };
                    }
                    return n;
                  }),
                );
              })
              .catch(() => {
                setNotification?.(
                  'error',
                  `There was an error when trying to unsubscribe from the ${modalTarget[0].title} newsletter.`,
                );
                setLoading('none');
              });
            setIsModalOpen(false);
          },
          title: 'UNSUBSCRIBE',
        },
        onClose: () => setIsModalOpen(false),
        secondButton: {
          $variant: 'primary',
          onClick: () => setIsModalOpen(false),
          title: 'CANCEL',
        },
        title: 'Are you sure you want to unsubscribe?',
      },
      unsubscribeFromAll: {
        children: '',
        className: '',
        firstButton: {
          $variant: 'secondary',
          onClick: () => {
            setLoading('updating');
            const subscribedNewsletters = modalTarget
              .filter((nl) => nl.subscriptionStatus === 'subscribed')
              .map((newsletter) => newsletter.formName);
            unSubscribeAll(email, country.value, subscribedNewsletters);
            setIsModalOpen(false);
          },
          title: 'UNSUBSCRIBE',
        },
        onClose: () => setIsModalOpen(false),
        secondButton: {
          $variant: 'primary',
          onClick: () => setIsModalOpen(false),
          title: 'CANCEL',
        },
        title: 'Are you sure you want to unsubscribe?',
      },
    };
    return settings[modalType] as {
      children: JSX.Element | string;
      className: string;
      firstButton?: ModalButtonProps;
      secondButton?: ModalButtonProps;
      onClose: () => void;
      title: string;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modalTarget,
    modalType,
    inputEmail,
    inputCountry,
    checked,
    emailError,
    countryError,
    emailExists,
    email,
    showSuccessGuestRegistration,
  ]);

  return {
    isModalOpen,
    modalSettings,
    setIsModalOpen,
    setModalTarget,
    setModalType,
    showSuccessGuestRegistration,
  };
}
